import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  InputAdornment,
  OutlinedInput,
  InputLabel,
  FormControl,
  Link,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import {
  Button,
  ModalAddDepartment,
  ModalComponent,
  ModalEditDepartment,
  LoadingComponent,
} from '../../components';
import { getDepartment } from '../../API';
import { useDebounce } from 'src/hooks/useDebounce';
import { useHead } from 'src/context/HeadContext';

export default function Department() {
  const [id, setId] = useState('');
  const [search, setSearch] = useState('');
  const [isShowAddModal, setIsShowModal] = useState(false);
  const [isEditModalShow, setIsEditModalShow] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedValue = useDebounce(search, 500);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDebouncedSearch(debouncedValue);
  }, [debouncedValue]);

  const handleEditModal = (id) => {
    setId(id);
    setIsEditModalShow(true);
  };

  const getDepartmentList = async () => {
    try {
      setIsLoading(true);
      const response = await getDepartment();
      if (response.meta.code === 200) {
        setData(response?.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChildData = () => {
    setSearch('');
    getDepartmentList();
  };

  useEffect(() => {
    getDepartmentList();
  }, []);

  const filteredData = debouncedSearch
    ? data.filter(
        (item) =>
          item.code?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          item.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : data;

  const parentNameById = data.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {});

  const { helmet, favicon } = useHead();

  return (
    <>
      <Helmet>
        <title>{helmet.title}</title>
        <link rel="icon" type="image/png" sizes="32x32" href={favicon} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon} />
      </Helmet>

      <Box sx={{ overflow: 'hidden', px: 4, py: 3 }}>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={12} md={4} lg={6}>
                <></>
              </Grid>
              <Grid item xs={12} sm={6} md={5} lg={4}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel
                    sx={{
                      paddingRight: 5,
                    }}
                  >
                    Cari Kode/Nama
                  </InputLabel>
                  <OutlinedInput
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    endAdornment={
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    }
                    label="Cari Kode/Nama"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <Button
                  color="color"
                  variant="contained"
                  label="TAMBAH"
                  size="large"
                  onClick={() => setIsShowModal(true)}
                  fullWidth={true}
                  py={1}
                />
              </Grid>
            </Grid>
            <TableContainer
              component={Paper}
              sx={{
                overflowX: 'auto',
                width: '100%',
              }}
            >
              <Table sx={{ minWidth: 750, border: '1px solid #ccc' }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '5%' }}>#ID</TableCell>
                    <TableCell width={'15%'}>Kode</TableCell>
                    <TableCell width={'25%'}>Nama</TableCell>
                    <TableCell width={'25%'}>Departemen Induk</TableCell>
                    <TableCell width={'10%'}>Status</TableCell>
                    <TableCell width={'15%'}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell colSpan={6}>
                        <LoadingComponent />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length !== 0 ? (
                    <>
                      {filteredData.map((item, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{item.code}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            {item.under_department_id ? parentNameById[item.under_department_id] || '-' : '-'}
                          </TableCell>
                          <TableCell>{item.inactive_at === null ? 'Aktif' : 'Tidak Aktif'}</TableCell>
                          <TableCell>
                            <Link
                              sx={{ cursor: 'pointer' }}
                              underline="hover"
                              onClick={() => handleEditModal(item.id)}
                            >
                              [Lihat]
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                        Tidak ada data yang tersedia
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        <ModalComponent open={isShowAddModal} close={() => setIsShowModal(false)} title={'Tambah Departemen'}>
          <ModalAddDepartment
            departmentList={data}
            onClick={() => setIsShowModal(false)}
            onDataAdd={handleChildData}
          />
        </ModalComponent>
        <ModalComponent open={isEditModalShow} close={() => setIsEditModalShow(false)} title={'Edit Departemen'}>
          <ModalEditDepartment
            id={id}
            departmentList={data}
            onClick={() => setIsEditModalShow(false)}
            onDataUpdate={handleChildData}
          />
        </ModalComponent>
      </Box>
    </>
  );
}
